import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import { isMobile } from "react-device-detect";
import { Helmet } from "react-helmet";
import logoImage from "../../../assets/images/logo.png";
import invokeApi from "../../../services/invokeApi";

// Formatear monto entero a formato CLP con puntos (ej: 15000 → "$15.000")
const formatearMontoCLP = (monto) => {
  if (monto === null || monto === undefined) return "$0";
  return `$${parseInt(monto).toLocaleString('es-CL')}`;
};

// Mostrar precio o "GRATIS" según es_gratis
const mostrarPrecio = (esGratis, precioBase) => {
  if (esGratis) return "GRATIS";
  return formatearMontoCLP(precioBase);
};

// Extraer primer nombre y capitalizar: "PABLO MARTINEZ" → "Pablo"
const primerNombre = (nombreCompleto) => {
  if (!nombreCompleto) return "";
  const primer = nombreCompleto.trim().split(/\s+/)[0];
  return primer.charAt(0).toUpperCase() + primer.slice(1).toLowerCase();
};

// Llamada real a la API
const fetchCheckoutStatusWithAllParams = async (allParams) => {
  try {
    // Construir query string dinámicamente con todos los parámetros
    const queryString = Object.keys(allParams)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(allParams[key])}`)
      .join('&');
    
    const response = await invokeApi(`/agenda/contenidoCheckoutConfirm?${queryString}`, "get");
    
    // Verificar si hay error en la respuesta
    if (!response.success || response.statusCode !== 200) {
      return {
        statusCode: response.statusCode || 500,
        success: false,
        error: response.error || "Error al consultar el estado",
        mensaje: response.mensaje || "No logramos validar el pago, por favor intenta comprar nuevamente.",
        retry_url: `/contenido/${response.contenido_link || ""}`,
        contenido_nombre: response.contenido_nombre || "Contenido no disponible",
        es_gratis: response.es_gratis || false,
        precio_base: response.precio_base || 0,
        cargo_servicio: response.cargo_servicio || 0,
        precio_total: response.precio_total || 0,
        profesional_nombres: response.profesional_nombres || "No disponible",
        profesional_apellidos: response.profesional_apellidos || "",
        profesional_profesion: response.profesional_profesion || "",
        status: response.status || "Error",
        metodo_pago: response.metodo_pago || "No disponible",
        token: response.id_checkout || allParams.id_checkout || "No disponible",
        hora_checkout: response.hora_checkout || new Date().toLocaleString(),
        ultimos_4_digitos: response.ultimos_4_digitos || null,
        tipo_tarjeta: response.tipo_tarjeta || null,
        comprador_nombre: response.comprador_nombre || "",
        comprador_email: response.comprador_email || ""
      };
    }
    
    // Respuesta exitosa - usar estructura directa de la API
    return {
      statusCode: response.statusCode,
      success: response.success,
      contenido_nombre: response.contenido_nombre,
      es_gratis: response.es_gratis,
      precio_base: response.precio_base,
      cargo_servicio: response.cargo_servicio,
      precio_total: response.precio_total,
      profesional_nombres: response.profesional_nombres,
      profesional_apellidos: response.profesional_apellidos,
      profesional_profesion: response.profesional_profesion,
      comprador_nombre: response.comprador_nombre,
      comprador_email: response.comprador_email,
      status: response.status,
      metodo_pago: response.metodo_pago,
      divisa: response.divisa,
      token: response.id_checkout,
      hora_checkout: response.hora_checkout,
      retry_url: `/contenido/${response.contenido_link}`,
      ultimos_4_digitos: response.ultimos_4_digitos || null,
      tipo_tarjeta: response.tipo_tarjeta || null
    };
    
  } catch (error) {
    console.error("Error en fetchCheckoutStatusWithAllParams:", error);
    return {
      statusCode: 500,
      success: false,
      error: "Error del servidor",
      mensaje: "Hubo un problema técnico. Por favor intenta más tarde.",
      retry_url: "/contenido/9junh21k",
      contenido_nombre: "Contenido no disponible",
      es_gratis: false,
      precio_base: 0,
      cargo_servicio: 0,
      precio_total: 0,
      profesional_nombres: "No disponible",
      profesional_apellidos: "",
      profesional_profesion: "",
      status: "Error",
      metodo_pago: "No disponible",
      token: allParams.id_checkout || "No disponible",
      hora_checkout: new Date().toLocaleString(),
      ultimos_4_digitos: null,
      tipo_tarjeta: null,
      comprador_nombre: "",
      comprador_email: ""
    };
  }
};

function ContenidoStatus() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [apiResponse, setApiResponse] = useState(null);

  // Estados para bottom sheet de info cargo por servicio
  const [showInfoSheet, setShowInfoSheet] = useState(false);
  const [isClosingSheet, setIsClosingSheet] = useState(false);
  const sheetRef = useRef(null);

  const pageTitle = "Clinifica - Estado de Compra";
  const pageDescription = "Estado de tu compra de contenido especializado.";

  // Helper function para convertir todos los searchParams a objeto
  const getAllUrlParams = (searchParams) => {
    const params = {};
    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }
    return params;
  };

  // Memoizar parámetros de URL para evitar re-renders innecesarios
  const memoizedUrlParams = useMemo(() => {
    return getAllUrlParams(searchParams);
  }, [searchParams]);

  // Llamada real a API al cargar el componente
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await fetchCheckoutStatusWithAllParams(memoizedUrlParams);
        setApiResponse(response);
      } catch (error) {
        console.error("Error en API real:", error);
        setApiResponse({
          statusCode: 500,
          success: false,
          error: "Error del servidor",
          mensaje: "Hubo un problema técnico. Por favor intenta más tarde.",
          retry_url: "/contenido/9junh21k",
          contenido_nombre: "Contenido no disponible",
          es_gratis: false,
          precio_base: 0,
          cargo_servicio: 0,
          precio_total: 0,
          profesional_nombres: "No disponible",
          profesional_apellidos: "",
          profesional_profesion: "",
          status: "Error",
          metodo_pago: "No disponible",
          token: memoizedUrlParams.id_checkout || "No disponible",
          hora_checkout: new Date().toLocaleString(),
          ultimos_4_digitos: null,
          tipo_tarjeta: null,
          comprador_nombre: "",
          comprador_email: ""
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatus();
  }, [memoizedUrlParams]);

  // Función para cerrar bottom sheet con animación
  const closeSheet = useCallback(() => {
    setIsClosingSheet(true);
    setTimeout(() => {
      setShowInfoSheet(false);
      setIsClosingSheet(false);
    }, 220);
  }, []);

  // Cerrar al hacer click fuera de la caja
  useEffect(() => {
    if (!showInfoSheet) return;

    const handleClickOutside = (e) => {
      if (sheetRef.current && !sheetRef.current.contains(e.target)) {
        closeSheet();
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showInfoSheet, closeSheet]);

  // Verificar que tengamos al menos algún parámetro de transacción
  const hasRequiredParams = Object.keys(memoizedUrlParams).length > 0;
  
  if (!hasRequiredParams) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        
        <Box
          component="img"
          src={logoImage}
          alt="Clinifica Logo"
          sx={{
            width: { xs: "150px", md: "200px" },
            height: "auto",
            display: "block",
            mx: "auto",
            p: "15px",
          }}
        />
        
        <Box
          sx={{
            maxWidth: { xs: "94%", md: "800px" },
            width: { xs: "94%", md: "90%" },
            mx: "auto",
            bgcolor: "#fbfafe",
            boxShadow: 1,
            marginTop: 2,
            marginBottom: 8,
            borderRadius: 2,
            p: { xs: 2, md: 4 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { md: "16px", xs: "14px" },
              color: "#9e9e9e",
              textAlign: "center",
            }}
          >
            No se encontraron parámetros de transacción
          </Typography>
        </Box>
      </>
    );
  }

  const handleRetryPurchase = () => {
    if (apiResponse?.retry_url) {
      navigate(apiResponse.retry_url);
    }
  };

  // Determinar si mostrar desglose de precios (solo si no es gratis)
  const mostrarDesglose = apiResponse && !apiResponse.es_gratis;

  // Detectar error de API (sin datos reales) vs rechazo Transbank (con datos reales)
  // statusCode 200 con success false = rechazo Transbank (Lambda respondió con datos)
  // statusCode !== 200 con success false = error de API (no tenemos datos confiables)
  const isApiError = apiResponse && !apiResponse.success && apiResponse.statusCode !== 200;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>
      
      <Box
        component="img"
        src={logoImage}
        alt="Clinifica Logo"
        sx={{
          width: { xs: "150px", md: "200px" },
          height: "auto",
          display: "block",
          mx: "auto",
          p: "15px",
        }}
      />

      {/* Caja de resumen */}
      <Box
        translate="no"
        className="notranslate"
        sx={{
          maxWidth: { xs: "94%", md: "800px" },
          width: { xs: "94%", md: "90%" },
          mx: "auto",
          bgcolor: "#fbfafe",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 2,
          borderRadius: 2,
          p: { xs: 2, md: 4 },
        }}
      >
        {isLoading ? (
          <Skeleton animation="wave" variant="text" width={150} height={20} sx={{ mb: 0 }} />
        ) : (
          <Typography sx={{ 
            fontFamily: "Quicksand-Regular", 
            fontSize: { md: "16px", xs: "14px" },
            color: "#626262",
            mb: 0
          }}>
            {apiResponse?.success 
              ? "Compraste:" 
              : "Intentaste comprar:"
            }
          </Typography>
        )}
        
        {isLoading ? (
          <Skeleton animation="wave" variant="text" width={300} height={24} sx={{ mb: 1 }} />
        ) : (
          <Typography
            sx={{
              mt: -0.5,
              fontFamily: "Quicksand-Bold",
              fontSize: { xs: "18px", md: "20px" },
              color: "#191C1F",
              mb: 0
            }}
          >
            {apiResponse?.contenido_nombre}
          </Typography>
        )}

        {/* Pill "Contenido" — mismo estilo que ContenidoComprar */}
        {isLoading ? (
          <Skeleton animation="wave" variant="rounded" width={90} height={24} sx={{ mb: 1, borderRadius: 2 }} />
        ) : (
          <Box
            sx={{
              display: "inline-block",
              backgroundColor: "#f0f4ff",
              color: "#3B82F6",
              fontFamily: "Quicksand-Medium",
              fontSize: { md: "15px", xs: "13px" },
              borderRadius: 2,
              paddingLeft: 0.75,
              paddingRight: 0.75,
              paddingTop: 0.25,
              paddingBottom: 0.25,
              marginLeft: -0.75,
              mt: -0.75,
              textAlign: "left",
            }}
          >
            Contenido
          </Box>
        )}
        
        {isLoading ? (
          <Skeleton animation="wave" variant="text" width={100} height={20} sx={{ mb: 1 }} />
        ) : (
          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { md: "16px", xs: "14px" },
              color: "#191C1F",
              mt: 0,
              mb: 0
            }}
          >
            {mostrarPrecio(apiResponse?.es_gratis, apiResponse?.precio_base)}
          </Typography>
        )}
        
        {isLoading ? (
          <Skeleton animation="wave" variant="text" width={100} height={20} sx={{ mb: 0 }} />
        ) : (
          <Typography sx={{ 
            fontFamily: "Quicksand-Regular", 
            fontSize: { md: "16px", xs: "14px" },
            color: "#626262",
            mt: 2,
            mb: 0
          }}>
            Creado por:
          </Typography>
        )}
        
        {isLoading ? (
          <Skeleton animation="wave" variant="text" width={200} height={24} sx={{ mb: 0 }} />
        ) : (
          <Typography
            sx={{
              mt: -0.5,
              fontFamily: "Quicksand-Bold",
              fontSize: { xs: "18px", md: "20px" },
              color: "#191C1F",
              mb: 0
            }}
          >
            {`${apiResponse?.profesional_nombres} ${apiResponse?.profesional_apellidos}`}
          </Typography>
        )}
        
        {isLoading ? (
          <Skeleton animation="wave" variant="text" width={120} height={20} sx={{ mb: 0 }} />
        ) : (
          apiResponse?.profesional_profesion && (
            <Typography sx={{ 
              fontFamily: "Quicksand-Regular", 
              mt: -0.5,
              fontSize: { md: "16px", xs: "14px" },
              color: "#191C1F",
              mb: 0
            }}>
              {apiResponse?.profesional_profesion}
            </Typography>
          )
        )}
      </Box>

      {isLoading ? (
        <Skeleton animation="wave" variant="text" width={250} height={40} sx={{ mx: "auto", mt: 3, mb: 1 }} />
      ) : (
        <Typography
          sx={{
              fontFamily: "Quicksand-Bold",
              fontSize: { md: "28px", xs: "24px" },
              textAlign: "center",
              color: "#191C1F",
              mt: 3,
              mb: 1
          }}
        >
          Estado de tu compra
        </Typography>
      )}

      <Box
        sx={{
          maxWidth: { xs: "94%", md: "800px" },
          width: { xs: "94%", md: "90%" },
          mx: "auto",
          bgcolor: "white",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 8,
          borderRadius: 2,
          p: { xs: 2, md: 4 },
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          minHeight: "125px",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 2,
              width: "100%",
            }}
          >
            <Skeleton animation="wave" variant="text" width={isMobile ? "90%" : 350} height={24} />
            <Box
              sx={{
                width: "33%",
                height: "1px",
                backgroundColor: "#e0e0e0",
                mb: 1,
                alignSelf: "center",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1,
                width: "100%",
                maxWidth: "400px",
              }}
            >
              <Skeleton animation="wave" variant="text" width={200} height={20} />
              <Skeleton animation="wave" variant="text" width={150} height={20} />
              <Skeleton animation="wave" variant="text" width={180} height={20} />
              <Skeleton animation="wave" variant="text" width={220} height={20} />
              <Skeleton animation="wave" variant="text" width={240} height={20} />
              <Skeleton animation="wave" variant="text" width={200} height={20} />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "left",
              width: "100%",
            }}
          >
            {/* Contenedor centrado — coherente con ContenidoComprar */}
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: "100%", md: "66.67%" },
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                boxSizing: "border-box",
              }}
            >
            {apiResponse?.success ? (
              // Caso de éxito
              <>
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Regular",
                    fontSize: { md: "16px", xs: "14px" },
                    color: "#191C1F",
                    mb: 3,
                    lineHeight: 1.6,
                    textAlign: "left",
                  }}
                >
                  <Box component="span" sx={{ fontFamily: "Quicksand-Bold" }}>{primerNombre(apiResponse.comprador_nombre)}</Box>, hemos enviado correo con un paso a paso de como acceder a tu contenido a <Box component="span" sx={{ fontFamily: "Quicksand-Bold" }}>{apiResponse.comprador_email}</Box>, recuerda revisar tu bandeja de entrada y carpetas de spam.
                </Typography>

                {/* Desglose de precios (solo si no es gratis) */}
                {mostrarDesglose && (
                  <Box sx={{ width: "100%", mb: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                      <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { md: "16px", xs: "14px" }, color: "#191C1F" }}>
                        Contenido digital
                      </Typography>
                      <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { md: "16px", xs: "14px" }, color: "#191C1F" }}>
                        {formatearMontoCLP(apiResponse.precio_base)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { md: "16px", xs: "14px" }, color: "#191C1F" }}>
                          Cargo por servicio
                        </Typography>
                        <InfoIcon
                          onClick={() => setShowInfoSheet(true)}
                          sx={{
                            fontSize: { md: "18px", xs: "16px" },
                            color: "#D6D6D6",
                            cursor: "pointer",
                          }}
                        />
                      </Box>
                      <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { md: "16px", xs: "14px" }, color: "#191C1F" }}>
                        {formatearMontoCLP(apiResponse.cargo_servicio)}
                      </Typography>
                    </Box>
                    <Box sx={{ borderTop: "1px solid #d0d0d0", my: 1 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ fontFamily: "Quicksand-Bold", fontSize: { md: "18px", xs: "16px" }, color: "#191C1F" }}>
                        Total pagado
                      </Typography>
                      <Typography sx={{ fontFamily: "Quicksand-Bold", fontSize: { md: "18px", xs: "16px" }, color: "#191C1F" }}>
                        {formatearMontoCLP(apiResponse.precio_total)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Caja de tarjeta — solo si no es gratis (pago con Transbank) */}
                {!apiResponse.es_gratis && (
                  <Box
                    sx={{
                      width: "100%",
                      boxSizing: "border-box",
                      border: "1px solid #e5e7eb",
                      borderRadius: 2.5,
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      {/* Ícono tarjeta verde */}
                      <Box
                        sx={{
                          width: 40,
                          height: 28,
                          borderRadius: 1,
                          backgroundColor: "#E8F5E8",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
                          <rect x="0.5" y="0.5" width="19" height="15" rx="2.5" stroke="#2E7D32" />
                          <rect x="0" y="4" width="20" height="3" fill="#2E7D32" />
                          <rect x="3" y="10" width="6" height="2" rx="1" fill="#2E7D32" />
                        </svg>
                      </Box>
                      <Typography sx={{ fontFamily: "Quicksand-Medium", fontSize: { md: "14px", xs: "13px" }, color: "#191C1F" }}>
                        {apiResponse.ultimos_4_digitos
                          ? `Tarjeta de ${apiResponse.tipo_tarjeta || "tarjeta"} terminada en ${apiResponse.ultimos_4_digitos}`
                          : "Pago aprobado"
                        }
                      </Typography>
                    </Box>
                    {/* Check verde */}
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        backgroundColor: "#E8F5E8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M5 13l4 4L19 7" stroke="#2E7D32" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Box>
                  </Box>
                )}

                {/* Referencia */}
                {apiResponse.token && (
                  <Typography
                    sx={{
                      fontFamily: "Quicksand-Regular",
                      fontSize: { md: "12px", xs: "11px" },
                      color: "#9ca3af",
                      textAlign: "center",
                      mt: 1,
                    }}
                  >
                    Referencia: {apiResponse.token}
                  </Typography>
                )}
              </>
            ) : isApiError ? (
              // Caso de error de API — sin datos reales
              <>
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Regular",
                    fontSize: { md: "16px", xs: "14px" },
                    color: "#191C1F",
                    lineHeight: 1.6,
                    textAlign: "left",
                  }}
                >
                  Ocurrió un error inesperado y no logramos procesar tu compra. No se ha realizado ningún cobro. Por favor vuelve a intentarlo nuevamente.
                </Typography>
              </>
            ) : (
              // Caso de rechazo Transbank — con datos reales
              <>
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Regular",
                    fontSize: { md: "16px", xs: "14px" },
                    color: "#191C1F",
                    mb: 3,
                    lineHeight: 1.6,
                    textAlign: "left",
                  }}
                >
                  <Box component="span" sx={{ fontFamily: "Quicksand-Bold" }}>{primerNombre(apiResponse.comprador_nombre)}</Box>, tu pago no fue aprobado por tu banco y no se realizó ningún cobro a tu tarjeta. Puedes volver a intentarlo presionando el botón de abajo.
                </Typography>

                {/* Desglose de precios en error (solo si no es gratis) */}
                {mostrarDesglose && (
                  <Box sx={{ width: "100%", mb: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                      <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { md: "16px", xs: "14px" }, color: "#191C1F" }}>
                        Contenido digital
                      </Typography>
                      <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { md: "16px", xs: "14px" }, color: "#191C1F" }}>
                        {formatearMontoCLP(apiResponse.precio_base)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { md: "16px", xs: "14px" }, color: "#191C1F" }}>
                          Cargo por servicio
                        </Typography>
                        <InfoIcon
                          onClick={() => setShowInfoSheet(true)}
                          sx={{
                            fontSize: { md: "18px", xs: "16px" },
                            color: "#D6D6D6",
                            cursor: "pointer",
                          }}
                        />
                      </Box>
                      <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { md: "16px", xs: "14px" }, color: "#191C1F" }}>
                        {formatearMontoCLP(apiResponse.cargo_servicio)}
                      </Typography>
                    </Box>
                    <Box sx={{ borderTop: "1px solid #d0d0d0", my: 1 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ fontFamily: "Quicksand-Bold", fontSize: { md: "18px", xs: "16px" }, color: "#191C1F" }}>
                        Total
                      </Typography>
                      <Typography sx={{ fontFamily: "Quicksand-Bold", fontSize: { md: "18px", xs: "16px" }, color: "#191C1F" }}>
                        {formatearMontoCLP(apiResponse.precio_total)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Caja de tarjeta — pago rechazado */}
                <Box
                  sx={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1px solid #e5e7eb",
                    borderRadius: 2.5,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {/* Ícono tarjeta roja */}
                    <Box
                      sx={{
                        width: 40,
                        height: 28,
                        borderRadius: 1,
                        backgroundColor: "#FEE2E2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
                        <rect x="0.5" y="0.5" width="19" height="15" rx="2.5" stroke="#c62828" />
                        <rect x="0" y="4" width="20" height="3" fill="#c62828" />
                        <rect x="3" y="10" width="6" height="2" rx="1" fill="#c62828" />
                      </svg>
                    </Box>
                    <Typography sx={{ fontFamily: "Quicksand-Medium", fontSize: { md: "14px", xs: "13px" }, color: "#191C1F" }}>
                      Pago rechazado
                    </Typography>
                  </Box>
                  {/* X roja */}
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      backgroundColor: "#FEE2E2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                      <path d="M6 6l12 12M18 6L6 18" stroke="#c62828" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                  </Box>
                </Box>

                {/* Referencia */}
                {apiResponse.token && (
                  <Typography
                    sx={{
                      fontFamily: "Quicksand-Regular",
                      fontSize: { md: "12px", xs: "11px" },
                      color: "#9ca3af",
                      textAlign: "center",
                      mt: 1,
                      mb: 3,
                    }}
                  >
                    Referencia: {apiResponse.token}
                  </Typography>
                )}

                <Button
                  variant="contained"
                  onClick={handleRetryPurchase}
                  sx={{
                    fontFamily: "Quicksand-Bold",
                    backgroundColor: "#3B82F6",
                    "&:hover": { backgroundColor: "#2563EB" },
                    borderRadius: 2,
                    fontSize: { md: "14px", xs: "12px" },
                    px: 3,
                    py: 1.5,
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  Intentar nuevamente
                </Button>
              </>
            )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Overlay oscuro + Bottom sheet de info cargo por servicio */}
      {showInfoSheet && (
        <>
          {/* Backdrop oscuro */}
          <Box
            onClick={closeSheet}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.35)",
              zIndex: 1299,
              animation: isClosingSheet
                ? "fadeOutBackdropContenidoStatus 0.22s ease forwards"
                : "fadeInBackdropContenidoStatus 0.22s ease",
              "@keyframes fadeInBackdropContenidoStatus": {
                from: { opacity: 0 },
                to: { opacity: 1 },
              },
              "@keyframes fadeOutBackdropContenidoStatus": {
                from: { opacity: 1 },
                to: { opacity: 0 },
              },
            }}
          />
          <Box
            ref={sheetRef}
            sx={{
              position: "fixed",
              bottom: 0,
              left: isMobile ? 0 : "50%",
              transform: isMobile ? "none" : "translateX(-50%)",
              width: isMobile ? "100%" : "800px",
              maxWidth: isMobile ? "100%" : "800px",
              zIndex: 1300,
              backgroundColor: "#ffffff",
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
              p: 2.5,
              boxSizing: "border-box",
              animation: isClosingSheet
                ? "slideDownInfoContenidoStatus 0.22s ease-in forwards"
                : "slideUpInfoContenidoStatus 0.22s ease-out",
              "@keyframes slideUpInfoContenidoStatus": {
                from: {
                  transform: isMobile
                    ? "translateY(100%)"
                    : "translateX(-50%) translateY(100%)",
                },
                to: {
                  transform: isMobile
                    ? "translateY(0)"
                    : "translateX(-50%) translateY(0)",
                },
              },
              "@keyframes slideDownInfoContenidoStatus": {
                from: {
                  transform: isMobile
                    ? "translateY(0)"
                    : "translateX(-50%) translateY(0)",
                },
                to: {
                  transform: isMobile
                    ? "translateY(100%)"
                    : "translateX(-50%) translateY(100%)",
                },
              },
            }}
          >
            {/* Pill handle */}
            <Box sx={{ width: 36, height: 4, borderRadius: 2, backgroundColor: "#d1d5db", margin: "0 auto 20px" }} />

            {/* Texto explicativo */}
            <Box
              sx={{
                width: "100%",
                border: "1px solid #e5e7eb",
                borderRadius: "14px",
                px: 2,
                py: 1.5,
                fontFamily: "Quicksand-Regular",
                fontSize: { md: "16px", xs: "14px" },
                color: "#191C1F",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                mb: 1.25,
                textAlign: "center",
                lineHeight: 1.5,
              }}
            >
              Cargo por servicio asociado a la compra de contenido digital en la plataforma
            </Box>

            {/* Botón Entendido */}
            <Box
              onClick={closeSheet}
              sx={{
                width: "100%",
                border: "none",
                borderRadius: "14px",
                px: 2,
                py: 1.5,
                fontFamily: "Quicksand-Bold",
                fontSize: { md: "16px", xs: "14px" },
                color: "#ffffff",
                backgroundColor: "#3B82F6",
                boxSizing: "border-box",
                textAlign: "center",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                userSelect: "none",
                "&:hover": {
                  backgroundColor: "#2563EB",
                },
                "&:active": {
                  backgroundColor: "#2563EB",
                },
              }}
            >
              Entendido
            </Box>
          </Box>
        </>
      )}
    </>
  );
}

export default ContenidoStatus;
